import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, alpha, useTheme,
  TextField,
  Chip, LinearProgress, Alert, Divider, Paper,
  CircularProgress,
} from '@mui/material';
import {
  ReceiptLong, CalendarMonth, CheckCircle, WarningAmber, Sell,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeInvoiceService from '../../services/feeInvoiceService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import SearchableSelect from '../common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';

import { GeneratePreview } from '../../types/fees';
import { Session, SessionClass, SectionInstance } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  onClose: () => void;
  onGenerated: (result: { created: number; skipped: number; period: string }) => void;
  /** Pre-select scope when launched from the assignments page. */
  defaultSessionId?: string;
  defaultClassId?: string;
}

/**
 * GenerateInvoicesDialog — materialize one invoice per active assignment
 * whose snapshot has installments falling in the chosen month.
 *
 * UX beats
 * --------
 *   1. Pick session + class + month → preview auto-loads.
 *   2. KPIs surface: N active assignments, M already generated, K will
 *      generate. Projected total in currency.
 *   3. Confirm → single transaction. Success alert stays visible so the
 *      admin has a moment of certainty before closing.
 */
const GenerateInvoicesDialog: React.FC<Props> = ({
  open, onClose, onGenerated, defaultSessionId, defaultClassId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Catalogs
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Form
  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionInstanceId, setSectionInstanceId] = useState('');
  const [month, setMonth] = useState<string>(() => format(new Date(), 'yyyy-MM'));
  // Optional overrides. Empty string means "use server default": today for
  // issueDate, the school's due days after issue for dueDate.
  const [issueDate, setIssueDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');

  // Preview / commit
  const [preview, setPreview] = useState<GeneratePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  const currencyFmt = useCurrencyFormat();

  // ─── Catalogs ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalogs(true);
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCatalogs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    setSessionId(defaultSessionId || '');
    setClassId(defaultClassId || '');
    setSectionInstanceId('');
    setMonth(format(new Date(), 'yyyy-MM'));
    setIssueDate(format(new Date(), 'yyyy-MM-dd'));
    setDueDate('');
    setPreview(null);
    setResult(null);
    setPreviewError(null);
  }, [open, defaultSessionId, defaultClassId]);

  useEffect(() => {
    if (!open || !sessionId || !classId) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await sectionInstancesApi.list({ sessionId, classId });
        if (!cancelled) setSections(list);
      } catch {
        if (!cancelled) setSections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, classId]);

  // ─── Preview ─────────────────────────────────────────────────────────────

  const canPreview = Boolean(sessionId && month);

  const runPreview = useCallback(async () => {
    if (!canPreview) return;
    setLoadingPreview(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await feeInvoiceService.previewGenerate({
        sessionId,
        classId: classId || null,
        sectionInstanceId: sectionInstanceId || null,
        month,
        issueDate: issueDate || null,
        dueDate: dueDate || null,
      });
      setPreview(res);
    } catch (e: any) {
      setPreviewError(e?.message || 'Could not compute preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [canPreview, sessionId, classId, sectionInstanceId, month, issueDate, dueDate]);

  useEffect(() => {
    if (canPreview) runPreview();
  }, [canPreview, runPreview]);

  // A new scope is a new run: drop the last result so Generate comes back.
  useEffect(() => { setResult(null); }, [sessionId, classId, sectionInstanceId, month]);

  // ─── Commit ─────────────────────────────────────────────────────────────

  const commit = async () => {
    if (!preview || preview.willGenerate === 0) return;
    setCommitting(true);
    try {
      const res = await feeInvoiceService.generateInvoices({
        sessionId,
        classId: classId || null,
        sectionInstanceId: sectionInstanceId || null,
        month,
        issueDate: issueDate || null,
        dueDate:   dueDate   || null,
      });
      setResult({ created: res.created, skipped: res.skipped });
      runPreview();
      onGenerated({ created: res.created, skipped: res.skipped, period: res.period.label });
      enqueueSnackbar(`Generated ${res.created} challan${res.created === 1 ? '' : 's'} for ${res.period.label}`, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to generate challans', { variant: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const monthLabel = useMemo(() => {
    if (!month) return '';
    try {
      const [y, m] = month.split('-').map(Number);
      const d = new Date(Date.UTC(y, m - 1, 1));
      return format(d, 'MMM yyyy');
    } catch { return month; }
  }, [month]);

  return (
    <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ReceiptLong color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Generate challans for a period</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Materialize one challan per active assignment whose installments fall in the chosen month. Safe to re-run — challans already generated for the same period are skipped.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loadingCatalogs && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {/* Scope */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 1 }}>
            What to generate
          </Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <SearchableSelect
                label="Session"
                value={sessionId}
                onChange={v => { setSessionId(v); setClassId(''); setSectionInstanceId(''); }}
                options={buildSessionOptions(sessions, theme)}
                clearable={false}
                fullWidth
                minWidth={0}
              />
              <TextField
                size="small"
                label="Month"
                type="month"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={month}
                onChange={e => setMonth(e.target.value)}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <SearchableSelect
                label="Class"
                value={classId}
                onChange={v => { setClassId(v); setSectionInstanceId(''); }}
                options={classes.map(c => ({ value: c._id, label: c.name }))}
                placeholder="All classes in this session"
                disabled={!sessionId}
                fullWidth
                minWidth={0}
              />
              <SearchableSelect
                label="Section"
                value={sectionInstanceId}
                onChange={setSectionInstanceId}
                options={sections.map(s => ({ value: s._id, label: s.name }))}
                placeholder={sections.length ? 'All sections' : 'No sections'}
                disabled={!classId || sections.length === 0}
                fullWidth
                minWidth={0}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                label="Issue date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={issueDate}
                onChange={e => setIssueDate(e.target.value)}
                helperText="Stamped on every challan as the issue date. Defaults to today."
              />
              <TextField
                size="small"
                label="Due date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                helperText="Leave blank to fall due the school's set number of days after the issue date (or after the month starts, when billing ahead)."
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Preview */}
        {loadingPreview && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Computing challans for {monthLabel}…
            </Typography>
          </Box>
        )}
        {previewError && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{previewError}</Alert>}

        {preview && !loadingPreview && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
              <Sell fontSize="small" sx={{ color: theme.palette.info.main }} />
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Preview
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                icon={<CalendarMonth sx={{ fontSize: '0.9rem !important' }} />}
                label={preview.period.label}
                size="small"
                variant="outlined"
              />
              <Chip label={`Due ${format(new Date(preview.dueDate), 'MMM d, yyyy')}`} size="small" variant="outlined" />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircle sx={{ fontSize: '0.9rem !important' }} />}
                label={`${preview.willGenerate} to generate`}
                size="small"
                variant="outlined"
                sx={{
                  height: 24, fontWeight: 600,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              {preview.alreadyGenerated > 0 && (
                <Chip
                  icon={<WarningAmber sx={{ fontSize: '0.9rem !important' }} />}
                  label={`${preview.alreadyGenerated} already generated`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 24, fontWeight: 600,
                    bgcolor: alpha(theme.palette.warning.main, 0.14),
                    color: theme.palette.warning.dark,
                    borderColor: alpha(theme.palette.warning.main, 0.34),
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {preview.activeAssignments} active assignment{preview.activeAssignments === 1 ? '' : 's'} in scope
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">Projected billing</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.dark">
                  {currencyFmt(preview.projectedTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total net across all new challans for {preview.period.label}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">Window</Typography>
                <Typography variant="body2">
                  {format(new Date(preview.period.startDate), 'MMM d, yyyy')}
                  {' → '}
                  {format(new Date(preview.period.endDate), 'MMM d, yyyy')}
                </Typography>
              </Box>
            </Stack>

            {preview.willGenerate === 0 && preview.activeAssignments > 0 && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                Every assignment in scope already has a challan for {preview.period.label}, or has no installments falling in this month.
              </Alert>
            )}
            {preview.activeAssignments === 0 && (
              <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                No active fee assignments match this scope. Assign a plan first.
              </Alert>
            )}
          </Paper>
        )}

        {committing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Generating challans…
            </Typography>
          </Box>
        )}

        {result && (
          <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
            Generated <strong>{result.created}</strong> challan{result.created === 1 ? '' : 's'}.
            {result.skipped > 0 ? ` Skipped ${result.skipped} that already existed.` : ''}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={committing}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            disableElevation
            onClick={commit}
            disabled={committing || !preview || preview.willGenerate === 0}
          >
            {committing
              ? 'Generating…'
              : preview
                ? `Generate ${preview.willGenerate} challan${preview.willGenerate === 1 ? '' : 's'}`
                : 'Generate'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateInvoicesDialog;
